import { useEffect, useMemo, useRef, useState } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Input } from '@headlessui/react';
import chevronDown from '@/assets/chevron_down.svg';
import { formatPhoneNumber } from '@/utils/format';

interface Country {
  id: number;
  name: string;
  code: string;
}

const countries: Country[] = [
  { id: 1, name: 'United States', code: '+1' },
  { id: 2, name: 'Canada', code: '+1' },
  { id: 3, name: 'Italy', code: '+39' },
  { id: 4, name: 'United Kingdom', code: '+44' },
  { id: 5, name: 'Australia', code: '+61' },
  { id: 6, name: 'New Zealand', code: '+64' },
];

interface PhoneInputProps {
  onChange?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
}

const PhoneInput = ({ onChange, onKeyDown, autoFocus = false }: PhoneInputProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [query, setQuery] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const filteredCountries = useMemo(() => {
    return query === ''
      ? countries
      : countries.filter((country) => {
          return country.name.toLowerCase().includes(query.toLowerCase());
        });
  }, [query]);

  const countryInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // autofocus phone number field on mount if requested
  useEffect(() => {
    if (autoFocus) {
      phoneInputRef.current?.focus();
    }
  }, [autoFocus]);

  const handleSelectCountry = (country: Country) => {
    if (!country) {
      return;
    }

    setSelectedCountry(country);
    if (onChange && phoneNumber) {
      onChange(`${country.code}${formatPhoneNumber(phoneNumber)}`);
    }
    setTimeout(() => {
      phoneInputRef.current?.focus();
    }, 0);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setPhoneNumber(formatPhoneNumber(newValue));
    if (onChange && newValue) {
      onChange(`${selectedCountry.code}${formatPhoneNumber(newValue)}`);
    }
  };

  return (
    <div className="w-full">
      <Combobox immediate value={selectedCountry} onChange={handleSelectCountry} onClose={() => setQuery('')}>
        <div className="relative">
          <ComboboxInput
            ref={countryInputRef}
            className="w-full rounded-md border-none bg-[#171717] h-16 px-6 text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25"
            aria-label="country"
            displayValue={(country: Country) => country?.name}
            onChange={(event) => setQuery(event.target.value)}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
            <img src={chevronDown} className="size-8 fill-white/60 group-data-hover:fill-white" />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          transition
          anchor="bottom"
          className="w-(--input-width) rounded-md border border-white/5 bg-[#171717] p-1 [--anchor-gap:--spacing(1)] empty:visible transition duration-100 ease-in data-leave:data-closed:opacity-0"
        >
          {filteredCountries.map((country) => (
            <ComboboxOption
              key={country.id}
              value={country}
              className="flex items-center h-16 px-6 rounded-md text-white data-focus:bg-stone-800"
            >
              {country.name}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </Combobox>
      <div className="mt-6 relative">
        <Input
          ref={phoneInputRef}
          type="tel"
          autoComplete="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          className="w-full rounded-md border-none bg-[#171717] h-16 pl-[80px] text-white focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25 text-[24px]/[40px] font-bold placeholder:text-[#ffffff40]"
          placeholder="123 456 7890"
          onKeyDown={onKeyDown}
        />
        <span className="absolute left-6 w-12 text-right top-1/2 -translate-y-1/2 text-white text-[24px]/[40px] font-bold">
          {selectedCountry?.code ?? '+1'}
        </span>
      </div>
    </div>
  );
};

export default PhoneInput;
